/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

/* eslint-disable no-continue */

import { IndividualSeriesOptions } from 'highcharts';
import Highcharts from 'highcharts-pack/highcharts/highcharts';
import { ILinkOptions } from 'src/adhoc/store/types/StateTypes';
import datasetTypes from '../../enums/datasetTypes';
import { TreeMapVisualization } from './TreeMapVisualization';
import genericChartVisualizationStaticPropertiesReducer from './chart/reducers/genericChartVisualizationReducer';
import { getEvents, getOutputParamFromMeasure } from './chart/util/EventUtil';
import { getMeasures } from './chart/util/GenericSeriesUtil';
import i18n from '../../../i18n/AdHocBundle.properties';

export class ParentTreeMapVisualization extends TreeMapVisualization {

    useShadow = false

    getChartSection() {
        const chart = super.getChartSection();

        return {
            ...chart,
            type: 'treemap',
            renderTo: 'chartContainer',
            zoomType: 'xy',
        };
    }

    getSeriesSection(selectedColIndex: number, selectedRowIndex: number): IndividualSeriesOptions[] {

        const dataset = this.getDataset();
        const linkOptions :ILinkOptions = this.getLinkOptions();
        const staticProperties = this.getStaticProperties();
        const measures = getMeasures(staticProperties.levelsStructure);
        const measure = measures[0];
        const levelsAmount = dataset.summary.rows.items.length;

        const results: any[][] = new Array(levelsAmount);
        for (let i = 0; i < levelsAmount; i += 1) {
            results[i] = [];
        }
        dataset.data.rows.children.forEach((child: any) => {
            this.exploreSeries(results, 0, child, [], [], dataset.data.aggregations, measure, dataset);
        });

        const seriesData: any[] = [];

        if (selectedRowIndex === 0) {
            const value = parseFloat(dataset.data.aggregations[0][0]);
            seriesData.push({
                id: `${i18n['adhoc.node.total.node']}`,
                name: i18n['adhoc.node.total.node'],
                measureLabel: measure.label,
                tooltipLabel: i18n['adhoc.node.total.node'],
                color: Highcharts.getOptions().colors[0],
                value,
                linkOutputParams: {
                    // the TreeMap has always one measure
                    measures: [getOutputParamFromMeasure(measure)]
                }
            });
        } else {

            // Parent Tree Map renders only two levels: the current and the previous (the parent).
            // Meaning: if you have next levels: [Totals(0), Continent(1), Country(2), City(3)]
            // and your row data level selector is set on Country (has value of 2) then Parent Tree Map
            // chart would render Continent and Country levels.
            // On other hand, Tree Map would render all levels.
            const startingRowDataLevel = Math.max(0, selectedRowIndex - 2);

            for (let currentLevel = startingRowDataLevel; currentLevel < selectedRowIndex; currentLevel += 1) {
                const nodes = results[currentLevel];

                for (let k = 0; k < nodes.length; k += 1) {
                    let node = nodes[k];

                    if (!node.value) {
                        // some nodes are coming with absent 'value' key, just because they don't have values from
                        // database, but to maintain consistency we need to set value to 0:
                        node.value = 0;
                    }

                    // now, for nodes which are top-level we need to remove the parent key, because this is what
                    // HC library needs from us. And to do this we need to make a copy of the node
                    if (currentLevel === startingRowDataLevel) {
                        node = { ...node };
                        delete node.parent;
                    }

                    seriesData.push(node);
                }
            }
        }

        // get the min and max values
        let
            firstMinMaxRecorded = false,
            min = 0,
            max = 0;
        if (seriesData.length) {
            min = seriesData[0].value;
            max = seriesData[0].value;
            for (let i = 1; i < seriesData.length; i += 1) {
                const { value } = seriesData[i];
                if (!firstMinMaxRecorded) {
                    min = value;
                    max = value;
                    firstMinMaxRecorded = true;
                } else {
                    min = Math.min(min, value);
                    max = Math.max(max, value);
                }
            }
        }

        // form the colors in the top-level cells only
        let colorIndex = 0;
        const colorPalette = Highcharts.getOptions().colors;
        for (let i = 0; i < seriesData.length; i += 1) {
            const cell = seriesData[i];
            if (cell.parent) {
                continue;
            }
            cell.color = colorPalette[colorIndex % colorPalette.length];
            colorIndex += 1;
        }

        const series = [
            {
                borderWidth: 1,
                dataLabels: {
                    enabled: false
                },
                name: staticProperties.levelsStructure.rows[0].label,
                cursor: 'pointer',
                data: seriesData,
                point: {
                    events: getEvents(linkOptions),
                },
                layoutAlgorithm: 'squarified',
                chartType: 'treemap',
                allowTraversingTree: true,
                levels: [
                    {
                        level: 1,
                        dataLabels: {
                            enabled: true
                        },
                        borderWidth: 3
                    }
                ],
                levelIsConstant: false
            }
        ];

        this.recordMinMaxValues(min, max);

        return series;
    }
}

export const ParentTreeMapVisualizationDescriptor = {
    legacyAdhoc: 'one_parent_tree_map',
    parentClass: 'Range',
    cssClass: 'ParentTreeMap',
    bundleName: 'adhoc.visualisation.chooser.type.ONE_PARENT_TREE_MAP',
    name: 'OneParentTreeMap',
    group: 'adhoc.visualisation.chooser.type.GROUP_RANGE',
    datasetType: datasetTypes.MULTI_AXIS,
    component: ParentTreeMapVisualization,
    staticPropertiesReducer: genericChartVisualizationStaticPropertiesReducer,
    requirements: {
        measures: {
            min: 1,
            max: 1,
            inRow: false
        },
        fields: {
            min: 2,
            inColumn: false
        }
    }
};
